import { DEFAULT_API_CONFIG } from './apiConfig';
import queryString from 'query-string';

const { url, getHeaders } = DEFAULT_API_CONFIG;
const baseRoute = `${url}/CallAgent`;
const externalBaseRoute = `${url}/ExternalServices`;

/**
 * Initiates a call for a specific order line
 * @param {number} orderLineId - The order line ID to initiate a call for
 * @returns {Promise<Object>} - The result of the call initiation
 */
export const initiateCall = async orderLineId => {
  const endpoint = `${baseRoute}/InitiateCall/${orderLineId}`;
  const headers = getHeaders();

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
    });

    const json = await response.json();
    return response.ok ? json : Promise.reject(json);
  } catch (error) {
    return Promise.reject(error);
  }
};

export const initiateAutomatedCalls = async () => {
  const endpoint = `${externalBaseRoute}/initiateAutomatedCalls`;
  const headers = getHeaders();

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
    });

    const json = await response.json();
    return response.ok ? json : Promise.reject(json);
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Gets a list of orders that need to be called
 * @param {boolean} isAuto - Whether to get auto call list
 * @returns {Promise<Array>} - List of orders that need to be called
 */
export const getCallList = async (isAuto = true, queryParams) => {
  const params = queryString.stringify({ isAuto, ...queryParams });
  const endpoint = `${baseRoute}/GetCallList?${params}`;
  const headers = getHeaders();

  try {
    const response = await fetch(endpoint, {
      method: 'GET',
      headers,
    });

    const json = await response.json();
    return response.ok ? json : Promise.reject(json);
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Processes auto calls based on current configurations
 * @returns {Promise<Object>} - Results of the auto call processing
 */
export const processAutoCalls = async () => {
  const endpoint = `${baseRoute}/ProcessAutoCalls`;
  const headers = getHeaders();

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
    });

    const json = await response.json();
    return response.ok ? json : Promise.reject(json);
  } catch (error) {
    return Promise.reject(error);
  }
};

/**
 * Logs a call for a specific order line
 * @param {number} orderLineId - The order line ID to log a call for
 * @param {string} callStatus - The status of the call
 * @param {string} [analysisResults] - Optional analysis results
 * @param {string} [narrative] - Optional narrative
 * @returns {Promise<Object>} - The result of the call logging
 */
export const logCall = async (orderLineId, callStatus, analysisResults = null, narrative = null) => {
  const params = queryString.stringify({
    callStatus,
    ...(analysisResults && { analysisResults }),
    ...(narrative && { narrative }),
  });

  const endpoint = `${baseRoute}/LogCall/${orderLineId}?${params}`;
  const headers = getHeaders();

  try {
    const response = await fetch(endpoint, {
      method: 'POST',
      headers,
    });

    const json = await response.json();
    return response.ok ? json : Promise.reject(json);
  } catch (error) {
    return Promise.reject(error);
  }
};
